import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import Header from "./Header.tsx";
import Sidebar from "./Sidebar.tsx";
import styles from "./PumpDashboard.module.css";
import useTheme from "../hooks/useTheme.ts";

interface InterlockRow {
  timestamp: string | null;
  fleet: number | null;
  unit: string | null;
  pump: string | null;
  interlockCode: string;
  severity: string;
  description: string;
}

const InterlocksSearch: React.FC = () => {
  const { theme } = useTheme();
  const isDarkTheme = theme === "dark";

  const [showSidebar, setShowSidebar] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 1024 : true,
  );

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:5000/api";

  const [fleet, setFleet] = useState("");
  const [unit, setUnit] = useState("");
  const [pump, setPump] = useState("");
  const [severity, setSeverity] = useState("");
  const [interlockCode, setInterlockCode] = useState("");
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<InterlockRow[]>([]);

  const formatDateParam = (date: Date | null) => {
    if (!date) return undefined;

    const pad = (value: number) => String(value).padStart(2, "0");

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate(),
    )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
      date.getSeconds(),
    )}`;
  };

  const searchInterlocks = async () => {
    try {
      setLoading(true);

      const token =
        localStorage.getItem("jwt") || localStorage.getItem("token");

      if (!token) {
        toast.error("Authentication token missing. Please sign in again.");
        setResults([]);
        return;
      }

      const response = await axios.get(`${backendUrl}/interlocks/search`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          fleet: fleet || undefined,
          unit: unit || undefined,
          pump: pump || undefined,
          severity: severity || undefined,
          interlock_code: interlockCode || undefined,
          start_time: formatDateParam(fromDate),
          end_time: formatDateParam(toDate),
        },
      });

      setResults(response.data || []);
    } catch (error) {
      console.error("Error searching interlocks:", error);
      toast.error("Unable to search interlocks. Please try again.");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFleet("");
    setUnit("");
    setPump("");
    setSeverity("");
    setInterlockCode("");
    setFromDate(null);
    setToDate(null);
    setResults([]);
  };

  return (
    <section
      className={`${styles.section} ${
        theme === "dark" ? "dark-theme" : "light-theme"
      }`}
    >
      <div className={styles.profile}>
        <Header
          title="Interlocks"
          setShowSidebar={setShowSidebar}
          showSidebar={showSidebar}
        />

        <div className={styles.container}>
          <div
            className={`${styles.sidebarStyle} app-sidebar-rail`}
            style={{
              width: showSidebar ? "224px" : "56px",
              transition: "width 0.3s",
            }}
          >
            <Sidebar />
          </div>

          <div className={styles.mainContentStyle}>
            <div className={styles.dashboardHeader}>
              <h1>Interlock Event Search</h1>

              <p>
                Search historical interlock events by fleet, unit, pump,
                severity, interlock code, and date range.
              </p>
            </div>

            <div className={styles.interlocksSearchCard}>
              <div className={styles.interlocksFilterGrid}>
                <input
                  value={fleet}
                  onChange={(e) => setFleet(e.target.value)}
                  placeholder="Fleet"
                  className={styles.interlocksFilterInput}
                />

                <input
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                  placeholder="Unit"
                  className={styles.interlocksFilterInput}
                />

                <input
                  value={pump}
                  onChange={(e) => setPump(e.target.value)}
                  placeholder="Pump"
                  className={styles.interlocksFilterInput}
                />

                <select
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value)}
                  className={styles.interlocksFilterInput}
                >
                  <option value="">All Severities</option>
                  <option value="Warning">Warning</option>
                  <option value="Critical">Critical</option>
                  <option value="Normal">Normal</option>
                </select>

                <input
                  value={interlockCode}
                  onChange={(e) => setInterlockCode(e.target.value)}
                  placeholder="Interlock Code"
                  className={styles.interlocksFilterInput}
                />

                <div className={styles.fleetDateInputWrap}>
                  <DatePicker
                    selected={fromDate}
                    onChange={(date) => setFromDate(date)}
                    showTimeSelect
                    dateFormat="MMM d, yyyy h:mm aa"
                    placeholderText="From Date"
                    className={styles.fleetDateTextInput}
                    wrapperClassName={styles.interlocksDatePickerWrapper}
                    popperClassName="fleetDatePickerPopper"
                    popperPlacement="bottom-start"
                    portalId="root"
                    timeIntervals={30}
                    timeFormat="h:mm aa"
                    timeCaption="Time"
                    calendarClassName={
                      isDarkTheme
                        ? "fleetDatePickerCalendar fleetDatePickerCalendarDark"
                        : "fleetDatePickerCalendar fleetDatePickerCalendarLight"
                    }
                  />
                </div>

                <div className={styles.fleetDateInputWrap}>
                  <DatePicker
                    selected={toDate}
                    onChange={(date) => setToDate(date)}
                    showTimeSelect
                    dateFormat="MMM d, yyyy h:mm aa"
                    placeholderText="To Date"
                    className={styles.fleetDateTextInput}
                    wrapperClassName={styles.interlocksDatePickerWrapper}
                    popperClassName="fleetDatePickerPopper"
                    popperPlacement="bottom-start"
                    portalId="root"
                    timeIntervals={30}
                    timeFormat="h:mm aa"
                    timeCaption="Time"
                    calendarClassName={
                      isDarkTheme
                        ? "fleetDatePickerCalendar fleetDatePickerCalendarDark"
                        : "fleetDatePickerCalendar fleetDatePickerCalendarLight"
                    }
                  />
                </div>
              </div>

              <div className={styles.interlocksActionRow}>
                <button
                  type="button"
                  className={styles.interlocksSearchBtn}
                  onClick={searchInterlocks}
                  disabled={loading}
                >
                  {loading ? "Searching..." : "Search"}
                </button>

                <button
                  type="button"
                  className={styles.interlocksClearBtn}
                  onClick={clearFilters}
                >
                  Clear
                </button>
              </div>
            </div>

            <div className={styles.tableContainer}>
              <div style={{ overflowX: "auto" }}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={styles.tableHeader}>Timestamp</th>
                      <th className={styles.tableHeader}>Fleet</th>
                      <th className={styles.tableHeader}>Unit</th>
                      <th className={styles.tableHeader}>Pump</th>
                      <th className={styles.tableHeader}>Interlock Code</th>
                      <th className={styles.tableHeader}>Severity</th>
                      <th className={styles.tableHeader}>Description</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={7} className={styles.noDataTable}>
                          Searching...
                        </td>
                      </tr>
                    ) : results.length === 0 ? (
                      <tr>
                        <td colSpan={7} className={styles.noDataTable}>
                          No interlocks found.
                        </td>
                      </tr>
                    ) : (
                      results.map((row, index) => (
                        <tr
                          key={`${row.timestamp}-${row.pump}-${index}`}
                          className={styles.tableRow}
                        >
                          <td className={styles.tableCell}>
                            {row.timestamp
                              ? new Date(row.timestamp).toLocaleString()
                              : "-"}
                          </td>
                          <td className={styles.tableCell}>
                            {row.fleet ?? "-"}
                          </td>
                          <td className={styles.tableCell}>
                            {row.unit || "-"}
                          </td>
                          <td className={styles.tableCell}>
                            {row.pump || "-"}
                          </td>
                          <td className={styles.tableCell}>
                            {row.interlockCode || "-"}
                          </td>
                          <td className={styles.tableCell}>
                            {row.severity || "-"}
                          </td>
                          <td className={styles.tableCell}>
                            {row.description || "-"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InterlocksSearch;
